import { generateUUIDv4 } from '@jwplayer/fe-common/module/util';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { JW_ACCOUNT_EDITIONS } from '/config/constants';
import { DebugStore } from '/debug/store';
import { SessionStore } from '/global-state/session';
import { StatusStore } from '/global-state/status';
import { useQuery } from '/module/query';

import { injectScript } from '../dom-utils';
import { useAbortController } from '../hooks';
import { useCurrentRoute } from '../routing/hooks';
import { isDefined } from '../types';

import { PageTargetedAlert } from './types';

const LOCALSTORAGE_KEY = 'dismissedAlertIds';

export const BOTR_API_READ_ONLY_ALERT = parsePageTargetedAlert({
  alert_id: 'PTA_BOTR_READ_ONLY',
  type: 'warning',
  body: 'JW Platform API is currently read-only. Some actions are temporarily available. Please try again in a few minutes.',
  title: 'JW Platform API is currently Read-Only',
  link_url: 'http://status.jwplayer.com/',
  link_text: 'View Status',
  dismissible: false,
});

export const PR_NUMBER_ALERT = parsePageTargetedAlert({
  alert_id: `PTA_PR_${import.meta.env.APP_PR_NUMBER}`,
  type: 'announcement',
  dismissible: true,
});

/**
 * Add default values and extra metadata to page targeted alert object.
 */
function parsePageTargetedAlert(alert: Partial<PageTargetedAlert>): PageTargetedAlert {
  return {
    // Default values
    target: 'all',
    accounts: JW_ACCOUNT_EDITIONS,
    alert_id: generateUUIDv4(),
    creation_date: new Date().toISOString(),
    dismissible: true,
    type: 'announcement',
    ...alert,
  };
}

function parseResponse(res: PageTargetedAlertsResponse): PageTargetedAlertsResponse {
  return {
    ...res,
    alerts: res.alerts.map((alert) => parsePageTargetedAlert(alert)),
  };
}

const getDismissedAlertIds = (): string[] => {
  const stored = window.localStorage.getItem(LOCALSTORAGE_KEY);
  if (!stored) {
    return [];
  }
  return stored
    .split(',')
    .filter((v) => v.trim())
    .filter(isDefined);
};

const storeDismissedAlertId = (ids: string | string[]) => {
  ids = Array.isArray(ids) ? ids : [ids];
  ids = ids.filter((v) => v.trim()).filter(isDefined);

  const currentIds = getDismissedAlertIds();
  // Make array unique
  const newIds = Array.from(new Set([...currentIds, ...ids]));

  window.localStorage.setItem(LOCALSTORAGE_KEY, newIds.join(','));
};

export type PageTargetedAlertsResponse = { alerts: PageTargetedAlert[]; total: number };

export const usePageTargetedAlerts = () => {
  const [dismissedIds, setDismissedIds] = useState<string[]>(getDismissedAlertIds());

  const abortController = useAbortController();

  const alertsQuery = useQuery(
    ['page-targeted-alerts'],
    async () => {
      const res = await (
        await fetch(`${import.meta.env.APP_API_URL}/v2/page_targeted_alerts/`, {
          signal: abortController?.signal,
        })
      ).json();

      return parseResponse(res);
    },
    {
      staleTime: 60 * 1000,
      refetchInterval: 30 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchIntervalInBackground: false,
      mock: {
        name: 'Page targeted alerts',
        description: 'Global banners on top of viewport',
        resources: async () => {
          return (await import('/debug/mocks/data/page-targeted-alerts')).default;
        },
      },
    },
  );

  // Use SessionStore directly instead of permissions hook because we
  // also need to be aware of users that are not logged in.
  const session = SessionStore.useState((s) => s.session);

  const currentRoute = useCurrentRoute();
  const location = useLocation();

  const botrApiReadOnly = StatusStore.useState((s) => s.botrStatus?.access_mode === 'read-only');
  const debugBotrApiReadOnly = DebugStore.useState((s) => s.readOnly === 'botr');

  const applicableAlerts = useMemo(() => {
    const applicableAlerts = (alertsQuery.data?.alerts ?? [])
      // Sanitize
      .filter((alert) => {
        if (alert === undefined) {
          return false;
        }

        const checkKeys: (keyof PageTargetedAlert)[] = ['type', 'body', 'target'];
        if (checkKeys.some((key) => typeof alert[key] === 'undefined')) {
          return false;
        }

        return true;
      })

      // Parse alert to make it work with the React router
      .map((alert) => parsePageTargetedAlert(alert))

      // Filter by date
      .filter((alert) => {
        const now = Date.now();

        if (alert.start_date && Date.parse(alert.start_date) > now) {
          return false;
        }

        if (alert.expire_date && Date.parse(alert.expire_date) <= now) {
          return false;
        }

        return true;
      })

      // Filter dismissed alerts
      .filter((alert) => {
        return alert.alert_id && !dismissedIds.includes(alert.alert_id);
      })

      // Filter by matching routes
      .filter((alert) => {
        const showEverywhere = alert.target === 'all';
        const matchesCurrentSection = currentRoute?.name.includes(alert.target); // i.e. target `account` in current route `accountBilling`

        if (showEverywhere || matchesCurrentSection) {
          return true;
        }

        return false;
      })

      // Filter by user/permissions
      .filter((alert) => {
        // If an alert targets a specific user type (playerEdition)
        if (Array.isArray(alert.accounts) && alert.accounts.length) {
          // If there's no session, filter the alert
          if (!session) {
            return false;
          }

          // Filter alerts for users who are not targeted by playerEdition
          if (!alert.accounts.includes(session.player_edition)) {
            return false;
          }
        }

        // Don't filter
        return true;
      })

      // Filter out NPS score survey (handled by SurveyMonkey)
      .filter((alert) => {
        if (alert.type === 'nps') {
          // Check if script has already been injected
          const id = `surveyMonkeyNpsScript`;
          const npsScript = document.getElementById(id);

          if (!npsScript) {
            // Survey will only appear once unless smcx cookie expires or is deleted
            injectScript({
              id,
              async: true,
              src: `https://widget.surveymonkey.com/collect/website/js/tRaiETqnLgj758hTBazgd_2BGwMmZF1Yo9yOcMeAJCJj28IavhVQNj3U1694fMRFDV.js`,
            });
          }

          // Don't display any other banners
          return false;
        }

        return true;
      });

    // Add a non-dismissible alert when the BOTR API is read only
    if (botrApiReadOnly || debugBotrApiReadOnly) {
      applicableAlerts.push(BOTR_API_READ_ONLY_ALERT);
    }

    return applicableAlerts;
  }, [
    alertsQuery.data,
    currentRoute?.name,
    dismissedIds,
    botrApiReadOnly,
    debugBotrApiReadOnly,
    location.pathname,
    session,
  ]);

  const dismiss = useMemo(() => {
    return (id: string) => {
      storeDismissedAlertId(id);
      setDismissedIds((s) => [...s, id]);
    };
  }, []);

  return { alerts: applicableAlerts, dismiss };
};
